// mds-dojo — dijit/Dialog — FAITHFUL port of references/dijit/Dialog.js (OVERWRITES
// the earlier freestyle lite). Modal dialog: extends layout/ContentPane + _TemplatedMixin
// + form/_FormMixin + _DialogMixin + _CssStateMixin. Fade in/out via dojo/_base/fx,
// draggable title bar, focus trap, ESC-to-cancel, DialogUnderlay + DialogLevelManager.
// AMD → ESM + declare from the faithful runtime.
//
// Faithful adaptations (flagged):
//  1. dojo/dnd/TimedMoveable is NOT ported to mds (it only existed to throttle IE6).
//     The `(has("ie") == 6) ? TimedMoveable : Moveable` choice is collapsed to plain
//     Moveable (evergreen). Behaviour identical on modern browsers.
//  2. dojo/Deferred → ../dojo/_base/Deferred (mds ships the legacy Deferred, whose
//     canceller-constructor + .promise/.then/.cancel surface Dialog uses is faithful).
//  3. dojo/i18n!./nls/common → the "dijit/common" strings are inlined (buttonOk/
//     buttonCancel/buttonSave/itemClose) and mixed in by postMixInProperties exactly
//     as i18n.getLocalization("dijit","common") did.
//  4. dijit/_base/manager is not ported; its ONLY member used here (defaultDuration)
//     is inlined from dojo/_base/config (config.defaultDuration || 200), matching
//     dijit/_base/manager.js.
//  5. has("dojo-bidi") + has("dijit-legacy-requires") blocks dropped; a11yclick +
//     dojo/ready + require imports dropped (template's ondijitclick is handled by
//     mds _AttachMixin mapping dijitclick→click; legacy TooltipDialog preload unneeded).
import array from '../dojo/_base/array';
import aspect from '../dojo/aspect';
import declare from '../dojo/_base/declare-runtime';
import Deferred from '../dojo/_base/Deferred';
import dom from '../dojo/dom';
import domClass from '../dojo/dom-class';
import domGeometry from '../dojo/dom-geometry';
import domStyle from '../dojo/dom-style';
import fx from '../dojo/_base/fx';
import keys from '../dojo/keys';
import lang from '../dojo/_base/lang';
import on from '../dojo/on';
import has from '../dojo/has';
import touch from '../dojo/touch';
import winUtils from '../dojo/window';
import Moveable from '../dojo/dnd/Moveable';
import config from '../dojo/_base/config';
import focus from './focus';
import _TemplatedMixin from './_TemplatedMixin';
import _CssStateMixin from './_CssStateMixin';
import _FormMixin from './form/_FormMixin';
import _DialogMixin from './_DialogMixin';
import DialogUnderlay from './DialogUnderlay';
import ContentPane from './layout/ContentPane';
import utils from './layout/utils';
import template from './templates/Dialog.html?raw';

// module:
//		dijit/Dialog

// dijit/_base/manager.defaultDuration, inlined (see header note 4).
var manager: any = { defaultDuration: (config as any).defaultDuration || 200 };

// dojo/i18n!./nls/common root strings, inlined (see header note 3).
var _commonNls: any = { buttonOk: 'OK', buttonCancel: 'Cancel', buttonSave: 'Save', itemClose: 'Close' };

var resolvedDeferred: any = new (Deferred as any)();
resolvedDeferred.resolve(true);

function nop() { /* no-op errback */ }

var _DialogBase: any = declare('dijit._DialogBase', [_TemplatedMixin, _FormMixin, _DialogMixin, _CssStateMixin], {
  templateString: template,

  baseClass: 'dijitDialog',

  cssStateNodes: {
    closeButtonNode: 'dijitDialogCloseIcon'
  },

  // Map widget attributes to DOMNode attributes.
  _setTitleAttr: { node: 'titleNode', type: 'innerHTML' },

  // open: [readonly] Boolean
  //		True if Dialog is currently displayed on screen.
  open: false,

  // duration: Integer
  //		The time in milliseconds it takes the dialog to fade in and out
  duration: manager.defaultDuration,

  // refocus: Boolean
  refocus: true,

  // autofocus: Boolean
  autofocus: true,

  // _firstFocusItem: [private readonly] DomNode
  _firstFocusItem: null,

  // _lastFocusItem: [private readonly] DomNode
  _lastFocusItem: null,

  // draggable: Boolean
  draggable: true,
  _setDraggableAttr: function (this: any, /*Boolean*/ val: any) {
    // Avoid _WidgetBase behavior of copying draggable attribute to this.domNode,
    // as that prevents text select on modern browsers (#14452)
    this._set('draggable', val);
  },

  // maxRatio: Number
  maxRatio: 0.9,

  // closable: Boolean
  closable: true,
  _setClosableAttr: function (this: any, val: any) {
    this.closeButtonNode.style.display = val ? '' : 'none';
    this._set('closable', val);
  },

  postMixInProperties: function (this: any) {
    var _nlsResources = _commonNls;
    lang.mixin(this, _nlsResources);
    this.inherited(arguments);
  },

  postCreate: function (this: any) {
    domStyle.set(this.domNode, {
      display: 'none',
      position: 'absolute'
    });
    this.ownerDocumentBody.appendChild(this.domNode);

    this.inherited(arguments);

    aspect.after(this, 'onExecute', lang.hitch(this, 'hide'), true);
    aspect.after(this, 'onCancel', lang.hitch(this, 'hide'), true);
    on(this.closeButtonNode, touch.press, function (e: any) {
      e.stopPropagation();
    });

    this._modalconnects = [];
  },

  onLoad: function (this: any) {
    // summary:
    //		Called when data has been loaded from an href.

    // when href is specified we need to reposition the dialog after the data is loaded
    // and find the focusable elements
    this.resize();
    this._position();

    if (this.autofocus && DialogLevelManager.isTop(this)) {
      this._getFocusItems();
      focus.focus(this._firstFocusItem);
    }

    this.inherited(arguments);
  },

  focus: function (this: any) {
    this._getFocusItems();
    focus.focus(this._firstFocusItem);
  },

  _endDrag: function (this: any) {
    // summary:
    //		Called after dragging the Dialog. Saves the position of the dialog in the viewport,
    //		and also adjust position to be fully within the viewport.
    var nodePosition = domGeometry.position(this.domNode),
      viewport = winUtils.getBox(this.ownerDocument);
    nodePosition.y = Math.min(Math.max(nodePosition.y, 0), (viewport.h - nodePosition.h));
    nodePosition.x = Math.min(Math.max(nodePosition.x, 0), (viewport.w - nodePosition.w));
    this._relativePosition = nodePosition;
    this._position();
  },

  _setup: function (this: any) {
    // summary:
    //		Stuff we need to do before showing the Dialog for the first time.

    var node = this.domNode;

    if (this.titleBar && this.draggable) {
      // TimedMoveable (IE6 throttle) dropped; plain Moveable on evergreen — see header note 1.
      this._moveable = new (Moveable as any)(node, { handle: this.titleBar });
      aspect.after(this._moveable, 'onMoveStop', lang.hitch(this, '_endDrag'), true);
    } else {
      domClass.add(node, 'dijitDialogFixed');
    }

    this.underlayAttrs = {
      dialogId: this.id,
      'class': array.map(this['class'].split(/\s/), function (s: any) {
        return s + '_underlay';
      }).join(' '),
      _onKeyDown: lang.hitch(this, '_onKey'),
      ownerDocument: this.ownerDocument
    };
  },

  _size: function (this: any) {
    // TODO: remove for 2.0
    this.resize();
  },

  _position: function (this: any) {
    // summary:
    //		Position the dialog in the viewport.

    if (!domClass.contains(this.ownerDocumentBody, 'dojoMove')) {    // don't do anything if called during auto-scroll
      var node = this.domNode,
        viewport = winUtils.getBox(this.ownerDocument),
        p = this._relativePosition,
        bb = domGeometry.position(node),
        l = Math.floor(viewport.l + (p ? Math.min(p.x, viewport.w - bb.w) : (viewport.w - bb.w) / 2)),
        t = Math.floor(viewport.t + (p ? Math.min(p.y, viewport.h - bb.h) : (viewport.h - bb.h) / 2));

      domStyle.set(node, {
        left: l + 'px',
        top: t + 'px'
      });
    }
  },

  _onKey: function (this: any, /*Event*/ evt: any) {
    // summary:
    //		Handles the keyboard events for accessibility reasons

    if (evt.keyCode == keys.TAB) {
      this._getFocusItems();
      var node = evt.target;
      if (this._firstFocusItem == this._lastFocusItem) {
        // don't move focus anywhere, but don't allow browser to move focus off of dialog either
        evt.stopPropagation();
        evt.preventDefault();
      } else if (node == this._firstFocusItem && evt.shiftKey) {
        // if we are shift-tabbing from first focusable item in dialog, send focus to last item
        focus.focus(this._lastFocusItem);
        evt.stopPropagation();
        evt.preventDefault();
      } else if (node == this._lastFocusItem && !evt.shiftKey) {
        // if we are tabbing from last focusable item in dialog, send focus to first item
        focus.focus(this._firstFocusItem);
        evt.stopPropagation();
        evt.preventDefault();
      }
    } else if (this.closable && evt.keyCode == keys.ESCAPE) {
      this.onCancel();
      evt.stopPropagation();
      evt.preventDefault();
    }
  },

  show: function (this: any) {
    // summary:
    //		Display the dialog
    // returns: dojo/promise/Promise

    if (this.open) {
      return resolvedDeferred.promise;
    }

    if (!this._started) {
      this.startup();
    }

    // first time we show the dialog, there's some initialization stuff to do
    if (!this._alreadyInitialized) {
      this._setup();
      this._alreadyInitialized = true;
    }

    if (this._fadeOutDeferred) {
      // There's a hide() operation in progress, so cancel it, but still call DialogLevelManager.hide()
      this._fadeOutDeferred.cancel();
      DialogLevelManager.hide(this);
    }

    // Recenter Dialog if user scrolls browser.
    var win = winUtils.get(this.ownerDocument);
    this._modalconnects.push(on(win, 'scroll', lang.hitch(this, 'resize', null)));

    this._modalconnects.push(on(this.domNode, 'keydown', lang.hitch(this, '_onKey')));

    domStyle.set(this.domNode, {
      opacity: 0,
      display: ''
    });

    this._set('open', true);
    this._onShow(); // lazy load trigger

    this.resize();
    this._position();

    // fade-in Animation object, setup below
    var fadeIn: any;

    this._fadeInDeferred = new (Deferred as any)(lang.hitch(this, function (this: any) {
      fadeIn.stop();
      delete this._fadeInDeferred;
    }));
    this._fadeInDeferred.then(undefined, nop);	// avoid spurious CancelError message to console

    // If delay is 0, code below will delete this._fadeInDeferred instantly, so grab promise while we can.
    var promise = this._fadeInDeferred.promise;

    fadeIn = fx.fadeIn({
      node: this.domNode,
      duration: this.duration,
      beforeBegin: lang.hitch(this, function (this: any) {
        DialogLevelManager.show(this, this.underlayAttrs);
      }),
      onEnd: lang.hitch(this, function (this: any) {
        if (this.autofocus && DialogLevelManager.isTop(this)) {
          // find focusable items each time dialog is shown
          this._getFocusItems();
          focus.focus(this._firstFocusItem);
        }
        this._fadeInDeferred.resolve(true);
        delete this._fadeInDeferred;
      })
    }).play();

    return promise;
  },

  hide: function (this: any) {
    // summary:
    //		Hide the dialog
    // returns: dojo/promise/Promise

    // If we haven't been initialized yet then we aren't showing and we can just return.
    if (!this._alreadyInitialized || !this.open) {
      return resolvedDeferred.promise;
    }
    if (this._fadeInDeferred) {
      this._fadeInDeferred.cancel();
    }

    // fade-out Animation object, setup below
    var fadeOut: any;

    this._fadeOutDeferred = new (Deferred as any)(lang.hitch(this, function (this: any) {
      fadeOut.stop();
      delete this._fadeOutDeferred;
    }));
    this._fadeOutDeferred.then(undefined, nop);	// avoid spurious CancelError message to console

    // fire onHide when the promise resolves.
    this._fadeOutDeferred.then(lang.hitch(this, 'onHide'));

    // If delay is 0, code below will delete this._fadeOutDeferred instantly, so grab promise while we can.
    var promise = this._fadeOutDeferred.promise;

    fadeOut = fx.fadeOut({
      node: this.domNode,
      duration: this.duration,
      onEnd: lang.hitch(this, function (this: any) {
        this.domNode.style.display = 'none';
        DialogLevelManager.hide(this);
        this._fadeOutDeferred.resolve(true);
        delete this._fadeOutDeferred;
      })
    }).play();

    if (this._scrollConnected) {
      this._scrollConnected = false;
    }
    var h;
    while (h = this._modalconnects.pop()) {
      h.remove();
    }

    if (this._relativePosition) {
      delete this._relativePosition;
    }
    this._set('open', false);

    return promise;
  },

  resize: function (this: any, dim: any) {
    // summary:
    //		Called with no argument when viewport scrolled or viewport size changed.

    if (this.domNode.style.display != 'none') {

      this._checkIfSingleChild();

      if (!dim) {
        if (this._shrunk) {
          // If we earlier shrunk the dialog to fit in the viewport, reset it to its natural size
          if (this._singleChild) {
            if (typeof this._singleChildOriginalStyle != 'undefined') {
              this._singleChild.domNode.style.cssText = this._singleChildOriginalStyle;
              delete this._singleChildOriginalStyle;
            }
          }
          array.forEach([this.domNode, this.containerNode, this.titleBar, this.actionBarNode], function (node: any) {
            if (node) {	// because titleBar may not be defined
              domStyle.set(node, {
                position: 'static',
                width: 'auto',
                height: 'auto'
              });
            }
          });
          this.domNode.style.position = 'absolute';
        }

        // If necessary, shrink Dialog to fit in viewport and have some space around it.
        var viewport = winUtils.getBox(this.ownerDocument);
        viewport.w *= this.maxRatio;
        viewport.h *= this.maxRatio;

        var bb = domGeometry.position(this.domNode);
        this._shrunk = false;
        // First check and limit width, because limiting the width may increase the height due to word wrapping.
        if (bb.w >= viewport.w) {
          dim = {
            w: viewport.w
          };
          domGeometry.setMarginBox(this.domNode, dim);
          bb = domGeometry.position(this.domNode);
          this._shrunk = true;
        }
        // Now check and limit the height
        if (bb.h >= viewport.h) {
          if (!dim) {
            dim = {
              w: bb.w
            };
          }
          dim.h = viewport.h;
          this._shrunk = true;
        }
        if (dim) {
          if (!dim.w) {
            dim.w = bb.w;
          }
          if (!dim.h) {
            dim.h = bb.h;
          }
        }
      }

      // Code to run if user has requested an explicit size, or the shrinking code above set an implicit size
      if (dim) {
        // Set this.domNode to specified size
        domGeometry.setMarginBox(this.domNode, dim);

        // And then size this.containerNode
        var layoutNodes = [];
        if (this.titleBar) {
          layoutNodes.push({ domNode: this.titleBar, region: 'top' });
        }
        if (this.actionBarNode) {
          layoutNodes.push({ domNode: this.actionBarNode, region: 'bottom' });
        }
        var centerSize = { domNode: this.containerNode, region: 'center' };
        layoutNodes.push(centerSize);

        var contentDim = utils.marginBox2contentBox(this.domNode, dim);
        utils.layoutChildren(this.domNode, contentDim, layoutNodes);

        // And then if this.containerNode has a single layout widget child, size it too.
        if (this._singleChild) {
          var cb = utils.marginBox2contentBox(this.containerNode, centerSize);
          this._singleChild.resize({ w: cb.w, h: cb.h });
          // TODO: save original size for restoring it on another show()?
        } else {
          this.containerNode.style.overflow = 'auto';
          this._layoutChildren();		// send resize() event to all child widgets
        }
      } else {
        this._layoutChildren();		// send resize() event to all child widgets
      }

      if (!has('touch') && !dim) {
        // If the user has scrolled the viewport then reposition the Dialog.
        this._position();
      }
    }
  },

  _layoutChildren: function (this: any) {
    // Override _ContentPaneResizeMixin._layoutChildren because even when there's just a single layout child
    // widget, sometimes we don't want to size it explicitly.

    array.forEach(this.getChildren(), function (widget: any) {
      if (widget.resize) {
        widget.resize();
      }
    });
  },

  destroy: function (this: any) {
    if (this._fadeInDeferred) {
      this._fadeInDeferred.cancel();
    }
    if (this._fadeOutDeferred) {
      this._fadeOutDeferred.cancel();
    }
    if (this._moveable) {
      this._moveable.destroy();
    }
    var h;
    while (h = this._modalconnects.pop()) {
      h.remove();
    }

    DialogLevelManager.hide(this);

    this.inherited(arguments);
  }
});

var Dialog: any = declare('dijit.Dialog', [ContentPane, _DialogBase], {
  // summary:
  //		A modal dialog Widget.
});
Dialog._DialogBase = _DialogBase;	// for monkey patching and dojox/widget/DialogSimple

var DialogLevelManager: any = Dialog._DialogLevelManager = {
  // summary:
  //		Controls the various active "levels" on the page.

  _beginZIndex: 950,

  show: function (/*dijit/_WidgetBase*/ dialog: any, /*Object*/ underlayAttrs: any) {
    // summary:
    //		Call right before fade-in animation for new dialog.

    // Save current focus
    ds[ds.length - 1].focus = focus.curNode;

    // Set z-index a bit above previous dialog
    var zIndex = ds[ds.length - 1].dialog ? ds[ds.length - 1].zIndex + 2 : Dialog._DialogLevelManager._beginZIndex;
    domStyle.set(dialog.domNode, 'zIndex', zIndex);

    // Display the underlay, or if already displayed then adjust for this new dialog
    DialogUnderlay.show(underlayAttrs, zIndex - 1);

    ds.push({ dialog: dialog, underlayAttrs: underlayAttrs, zIndex: zIndex });
  },

  hide: function (/*dijit/_WidgetBase*/ dialog: any) {
    // summary:
    //		Called when the specified dialog is hidden/destroyed, after the fade-out animation ends.

    if (ds[ds.length - 1].dialog == dialog) {
      // Removing the top (or only) dialog in the stack, return focus to previous dialog

      ds.pop();

      var pd = ds[ds.length - 1];	// the new active dialog (or the base page itself)

      // Adjust underlay
      if (ds.length == 1) {
        // Returning to original page.  Hide the underlay.
        DialogUnderlay.hide();
      } else {
        // Popping back to previous dialog, adjust underlay.
        DialogUnderlay.show(pd.underlayAttrs, pd.zIndex - 1);
      }

      // Adjust focus.
      if (dialog.refocus) {
        var focusNode = pd.focus;
        if (pd.dialog && (!focusNode || !dom.isDescendant(focusNode, pd.dialog.domNode))) {
          pd.dialog._getFocusItems();
          focusNode = pd.dialog._firstFocusItem;
        }

        if (focusNode) {
          // Refocus the button that spawned the Dialog.
          try {
            focusNode.focus();
          } catch (e) {
          }
        }
      }
    } else {
      // Removing a dialog out of order (#9944, #10705).
      var idx = array.indexOf(array.map(ds, function (elem: any) {
        return elem.dialog;
      }), dialog);
      if (idx != -1) {
        ds.splice(idx, 1);
      }
    }
  },

  isTop: function (/*dijit/_WidgetBase*/ dialog: any) {
    // summary:
    //		Returns true if specified Dialog is the top in the task
    return ds[ds.length - 1].dialog == dialog;
  }
};

// Stack representing the various active "levels" on the page.
var ds: any = Dialog._dialogStack = [
  { dialog: null, focus: null, underlayAttrs: null }    // entry for stuff at z-index: 0
];

// If focus was accidentally removed from the dialog, then refocus.
focus.watch('curNode', function (attr: any, oldNode: any, node: any) {
  // Note: if no dialogs, ds.length==1 but ds[ds.length-1].dialog is null
  var topDialog = ds[ds.length - 1].dialog;

  // If a node was focused, and there's a Dialog currently showing, and not in the process of fading out...
  if (node && topDialog && !topDialog._fadeOutDeferred && node.ownerDocument == topDialog.ownerDocument) {
    // If the node that was focused is inside the dialog or in a popup, don't do anything.
    do {
      if (node == topDialog.domNode || domClass.contains(node, 'dijitPopup')) { return; }
    } while (node = node.parentNode);

    // Otherwise, return focus to the dialog.
    topDialog.focus();
  }
});

export default Dialog;
